import { useI18n } from 'vue-i18n'
import usePost from '../../core/composables/api/usePost'
import { useSnackStore } from '../../stores/snack.store'
import { computed, reactive, ref, watch } from 'vue'
import type { JSONContent } from '@tiptap/vue-3'
import { useRouter } from 'vue-router'
import { ApiRequestStatus } from '../../core/models/api/api-request-status'
import { useFeedbackTopicStore } from '@/stores/feedback-topic.store'
import { storeToRefs } from 'pinia'
import type { GatheringMechanism, Perspective } from '@/core/models/perspective'

const STRUCTURED_FEEDBACK_STEPS = {
  selectMembersAndTemplate: 'select-members-and-template',
  selectQuestionGroups: 'select-question-groups',
  selectPerspectives: 'select-perspectives',
  addMetadata: 'add-metadata',
} as const

type StructuredFeedbackStep =
  (typeof STRUCTURED_FEEDBACK_STEPS)[keyof typeof STRUCTURED_FEEDBACK_STEPS]

export const useStartFeedback = (callback?: () => void) => {
  const snackStore = useSnackStore()
  const { t } = useI18n()
  const router = useRouter()

  const mode = ref<'unstructured' | 'structured'>('unstructured')

  const feedbackTopicStore = useFeedbackTopicStore()
  const { templates, loadingStatus: templateLoadingState } = storeToRefs(feedbackTopicStore)
  const { loadTemplates } = feedbackTopicStore

  const feedbackReceiverIds = ref<string[]>([])

  const form = reactive<{
    title?: string
    message?: JSON
    feedbackReceiverId?: string
    templateIds: string[]
    questionGroupIds: string[]
    perspectiveKey?: string
  }>({
    title: undefined,
    message: undefined,
    feedbackReceiverId: undefined,
    templateIds: [],
    questionGroupIds: [],
    perspectiveKey: undefined,
  })

  const unstructuredFeedbackFormData = reactive<Partial<UnstructuredFeedbackFormData>>({
    feedbackReceiverId: undefined,
    text: undefined,
  })

  const filteredTemplates = computed(() =>
    templates.value.filter((template) =>
      template.perspectives.some((p) => p.allowedMechanisms.includes('app_user')),
    ),
  )

  const selectedTemplates = computed(() =>
    filteredTemplates.value.filter((template) => form.templateIds.includes(template.id)),
  )

  /** Perspectives available for in-app give-feedback: intersection across selected templates. */
  const availablePerspectives = computed((): Perspective[] => {
    if (selectedTemplates.value.length === 0) return []

    const [first, ...rest] = selectedTemplates.value
    let intersected: Perspective[] = first.perspectives
      .filter((p) => p.allowedMechanisms.includes('app_user'))
      .map((p) => ({
        ...p,
        allowedMechanisms: [...p.allowedMechanisms],
      }))

    for (const template of rest) {
      intersected = intersected
        .filter((p) => template.perspectives.some((tp) => tp.key === p.key))
        .map((p) => {
          const other = template.perspectives.find((tp) => tp.key === p.key)!
          const mechanisms = p.allowedMechanisms.filter((m) =>
            other.allowedMechanisms.includes(m),
          ) as GatheringMechanism[]
          return { ...p, allowedMechanisms: mechanisms }
        })
        .filter((p) => p.allowedMechanisms.includes('app_user'))
    }

    return intersected
  })

  watch(
    availablePerspectives,
    (perspectives) => {
      if (perspectives.length === 0) {
        form.perspectiveKey = undefined
        return
      }
      if (!form.perspectiveKey || !perspectives.some((p) => p.key === form.perspectiveKey)) {
        form.perspectiveKey = perspectives[0].key
      }
    },
    { immediate: true },
  )

  const configurableTemplates = computed(() => {
    return templates.value
      .filter((template) => form.templateIds.includes(template.id))
      .filter(
        (template) =>
          template.questionGroupSections &&
          template.questionGroupSections.filter((section) => !section.required).length > 0,
      )
  })

  const isConfigurable = computed(() => configurableTemplates.value.length > 0)

  const needsPerspectiveSelection = computed(() => availablePerspectives.value.length > 1)

  const structuredSteps = computed<StructuredFeedbackStep[]>(() => [
    STRUCTURED_FEEDBACK_STEPS.selectMembersAndTemplate,
    ...(isConfigurable.value ? [STRUCTURED_FEEDBACK_STEPS.selectQuestionGroups] : []),
    ...(needsPerspectiveSelection.value ? [STRUCTURED_FEEDBACK_STEPS.selectPerspectives] : []),
    STRUCTURED_FEEDBACK_STEPS.addMetadata,
  ])

  const stepIndex = ref(0)
  const step = computed(() => structuredSteps.value[stepIndex.value])
  const isFirstStep = computed(() => stepIndex.value === 0)
  const isLastStep = computed(() => stepIndex.value === structuredSteps.value.length - 1)

  watch(mode, (newMode) => {
    stepIndex.value = 0
    if (newMode === 'structured' && templateLoadingState.value != ApiRequestStatus.Success) {
      loadTemplates()
    }
  })

  watch(feedbackReceiverIds, (newFeedbackReceiverIds) => {
    form.feedbackReceiverId = newFeedbackReceiverIds[0]
    unstructuredFeedbackFormData.feedbackReceiverId = newFeedbackReceiverIds[0]
  })

  const errors = ref<ErrorMap>(new Map())

  const validateStep = () => {
    errors.value.clear()

    if (mode.value === 'unstructured') {
      if (!unstructuredFeedbackFormData.feedbackReceiverId) {
        errors.value.set('feedbackReceiverId', t('feedback.formValidation.members'))
      }
      if (!unstructuredFeedbackFormData.text) {
        errors.value.set('text', t('feedback.formValidation.unstructuredFeedback'))
      }
      return
    }

    switch (step.value) {
      case STRUCTURED_FEEDBACK_STEPS.selectMembersAndTemplate:
        if (!form.feedbackReceiverId) {
          errors.value.set('feedbackReceiverId', t('feedback.formValidation.members'))
        }
        if (form.templateIds.length === 0) {
          errors.value.set('templateIds', t('feedback.formValidation.templates'))
        }
        if (form.templateIds.length > 0 && availablePerspectives.value.length === 0) {
          errors.value.set('perspectiveKey', t('startSurvey.gatheringRequired'))
        }
        break
      case STRUCTURED_FEEDBACK_STEPS.selectQuestionGroups:
        if (
          form.questionGroupIds.length === 0 &&
          !configurableTemplates.value.some((template) =>
            template.questionGroupSections?.some((section) => section.required),
          )
        ) {
          errors.value.set('questionGroupIds', t('feedback.formValidation.questionGroups'))
        }
        break
      case STRUCTURED_FEEDBACK_STEPS.selectPerspectives:
        if (!form.perspectiveKey || availablePerspectives.value.length === 0) {
          errors.value.set('perspectiveKey', t('startSurvey.gatheringRequired'))
        }
        break
    }
  }

  const nextStep = () => {
    validateStep()
    if (errors.value.size === 0 && !isLastStep.value) {
      stepIndex.value++
    }
  }

  const previousStep = () => {
    if (stepIndex.value > 0) {
      stepIndex.value--
      errors.value.clear()
    }
  }

  const getTemplateQuestionGroupIds = (templateId: string) => {
    return (
      templates.value
        .find((template) => template.id === templateId)
        ?.questionGroupSections?.flatMap((section) => section.questionGroups)
        .map((questionGroup) => questionGroup.id) ?? []
    )
  }

  watch(
    () => [...form.templateIds],
    (templateIds, previousTemplateIds = []) => {
      const addedTemplateIds = templateIds.filter((id) => !previousTemplateIds.includes(id))
      const removedTemplateIds = previousTemplateIds.filter((id) => !templateIds.includes(id))

      const addedQuestionGroupIds = addedTemplateIds.flatMap((templateId) =>
        getTemplateQuestionGroupIds(templateId),
      )
      form.questionGroupIds = Array.from(new Set([...form.questionGroupIds, ...addedQuestionGroupIds]))

      const removedQuestionGroupIds = new Set(
        removedTemplateIds.flatMap((templateId) => getTemplateQuestionGroupIds(templateId)),
      )
      form.questionGroupIds = form.questionGroupIds.filter((id) => !removedQuestionGroupIds.has(id))
    },
  )

  watch(structuredSteps, (newSteps) => {
    if (stepIndex.value >= newSteps.length) {
      stepIndex.value = Math.max(0, newSteps.length - 1)
    }
  })

  watch([form, unstructuredFeedbackFormData], () => {
    errors.value.clear()
  })

  const { mutate: postUnstructuredFeedback, status: unstructuredFeedbackState } = usePost<
    string,
    UnstructuredFeedbackFormData
  >('feedback/unstructured')

  const sendUnstructuredFeedback = async () => {
    validateStep()

    if (errors.value.size == 0) {
      await postUnstructuredFeedback({
        feedbackReceiverId: unstructuredFeedbackFormData.feedbackReceiverId!,
        text: unstructuredFeedbackFormData.text!,
      })
    }

    switch (unstructuredFeedbackState.value) {
      case ApiRequestStatus.Success:
        if (callback) {
          callback()
        }
        break
      case ApiRequestStatus.Error:
        snackStore.showError(t('feedback.giveFeedbackError'))
        break
    }
  }

  const {
    mutate: postStructuredFeedback,
    status: structuredFeedbackState,
    data: createdSurveyId,
  } = usePost<string, StructuredFeedbackFormData>('survey/feedback')

  const sendStructuredFeedback = async () => {
    validateStep()
    if (errors.value.size == 0) {
      await postStructuredFeedback({
        feedbackReceiverId: form.feedbackReceiverId!,
        templateIds: form.templateIds,
        questionGroupIds: form.questionGroupIds,
        perspectiveKey: form.perspectiveKey!,
        message: form.message,
        title: form.title,
      })
    }

    switch (structuredFeedbackState.value) {
      case ApiRequestStatus.Success:
        snackStore.showInfo(t('feedback.requestFeedbackSuccess'))
        const receiverId = form.feedbackReceiverId
        if (callback) {
          callback()
        }

        router.push({
          name: 'feedback',
          params: {
            surveyId: createdSurveyId.value,
            receiverId: receiverId,
          },
        })
        break
      case ApiRequestStatus.Error:
        snackStore.showError(t('feedback.requestFeedbackErrors'))
        break
    }
  }

  const clearForm = () => {
    errors.value.clear()
    form.title = undefined
    form.message = undefined
    form.feedbackReceiverId = undefined
    form.templateIds = []
    form.questionGroupIds = []
    form.perspectiveKey = undefined
    feedbackReceiverIds.value = []
    unstructuredFeedbackFormData.text = undefined
    unstructuredFeedbackFormData.feedbackReceiverId = undefined
    stepIndex.value = 0
  }

  return {
    mode,
    feedbackReceiverIds,
    form,
    filteredTemplates,
    configurableTemplates,
    availablePerspectives,
    templateLoadingState,
    errors,
    unstructuredFeedbackFormData,
    nextStep,
    previousStep,
    step,
    isFirstStep,
    isLastStep,
    sendUnstructuredFeedback,
    sendStructuredFeedback,
    clearForm,
  }
}

type StructuredFeedbackFormData = {
  title?: string
  message?: JSON
  feedbackReceiverId: string
  templateIds: string[]
  questionGroupIds: string[]
  perspectiveKey: string
}

type UnstructuredFeedbackFormData = {
  feedbackReceiverId: string
  text: JSONContent
}

type ErrorMap = Map<string, string>
